import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Snackbar,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { BookOpenCheckIcon, PlusIcon, TrendingDownIcon, TrendingUpDownIcon, TrendingUpIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { ApiClientError } from '../../hooks/useApiClient.ts';
import { useCalculatedDateRange } from '../../hooks/useCalculatedDateRange.ts';
import { useCombinedStatistics } from '../../hooks/useCombinedStatistics.ts';
import { useProcessedRaces } from '../../hooks/useProcessedRaces.ts';
import { useCaseApi } from '../../services/api/useCase.api.ts';
import { useFeedbackApi } from '../../services/api/useFeedback.api.ts';
import { Case } from '../../types/Case/Case.type.ts';
import { Disparity } from '../../types/Disparity/Disparity.type';
import { NewDisparity } from '../../types/Disparity/NewDisparity.type';
import { Statute } from '../../types/Statute.type';
import { ComparisonPoolType } from '../../types/enums/ComparisonPoolType.enum';
import { DisparitySubType } from '../../types/enums/DisparitySubType.enum.ts';
import { DisparityType } from '../../types/enums/DisparityType.enum.ts';
import { countStatuteStatistics } from '../../utils/countStatuteStatistics.ts';
import { formatDateOrNull, resolveInitialStartDate } from '../../utils/disparityDates.ts';
import { createEnhancementDyadsFromCase, EnhancementDyad, findDyadByStatuteIds } from '../../utils/enhancementDyads.ts';
import DisparityFieldsForm, { DisparityFieldsData } from '../Case/DisparityFieldsForm.tsx';
import DataAvailabilityHeatmap from '../DataAvailabilityHeatmap/DataAvailabilityHeatmap.tsx';
import ComparisonPoolSection from './ComparisonPoolSection';
import FeedbackSentDialog from './FeedbackSentDialog';
import NoComparisonDataDialog from './NoComparisonDataDialog';
import SendErrorReportDialog from './SendErrorReportDialog';

interface ComparatorToolProps {
  forNewDisparity?: boolean;
  countyId?: number;
  caseData?: Case;
  disparityStages?: Array<{ id: number; name: string }>;
  disparityTypes?: Array<{ id: number; name: string; subtypes?: Array<{ id: number; name: string }> }>;
  processedRaces?: Array<{ id: number; name: string }>;
  existingDisparity?: Disparity;
  initialDisparityFields?: Partial<DisparityFieldsData>;
  initialEnhancementDyad?: EnhancementDyad | null;
  initialComparisonPools?: NewDisparity['comparison_pools'];
  onApply?: (data: DisparityFieldsData & { comparisonPools: NewDisparity['comparison_pools'] }) => void;
  onError?: (error: Error) => void;
  onSuccess?: (disparity: Disparity) => void;
}

// Cap on total comparison cases (inclusion + exclusion combined) used for statutes/groups_statistics
const MAX_COMPARISON_CASES = 15;

const getDefaultDisparityFields = (initial?: Partial<DisparityFieldsData>): DisparityFieldsData => ({
  statuteIds: initial?.statuteIds ?? [],
  stageId: initial?.stageId ?? null,
  typeId: initial?.typeId ?? null,
  subtypeId: initial?.subtypeId ?? null,
  raceId: initial?.raceId ?? null,
  startDate: initial?.startDate ?? null,
  endDate: initial?.endDate ?? null,
});

function ComparatorTool({
  forNewDisparity,
  countyId,
  caseData,
  disparityStages,
  disparityTypes,
  processedRaces: configRaces,
  existingDisparity,
  initialDisparityFields,
  initialEnhancementDyad,
  initialComparisonPools,
  onApply,
  onError,
  onSuccess,
}: ComparatorToolProps) {
  const [openComparatorToolDialog, setOpenComparatorToolDialog] = useState(false);
  const [baseStatuteIds, setBaseStatuteIds] = useState<number[]>(() => {
    if (!existingDisparity?.comparison_pools) return [];
    const basePool = existingDisparity.comparison_pools.find(
      (pool) => pool.comparison_pool_type.id === ComparisonPoolType.BASE,
    );
    return basePool ? basePool.comparison_pool_statutes.map((s) => s.statute_id) : [];
  });
  const [comparatorCaseInputs, setComparatorCaseInputs] = useState<number[][]>(() => {
    if (!existingDisparity?.comparison_pools) return [];
    return existingDisparity.comparison_pools
      .filter((pool) => pool.comparison_pool_type.id === ComparisonPoolType.INCLUSION)
      .map((pool) => pool.comparison_pool_statutes.map((s) => s.statute_id));
  });
  const [exclusionCaseInputs, setExclusionCaseInputs] = useState<number[][]>(() => {
    if (!existingDisparity?.comparison_pools) return [];
    return existingDisparity.comparison_pools
      .filter((pool) => pool.comparison_pool_type.id === ComparisonPoolType.EXCLUSION)
      .map((pool) => pool.comparison_pool_statutes.map((s) => s.statute_id));
  });
  const [openAutocompleteId, setOpenAutocompleteId] = useState<string | null>(null);
  const [cachedStatutesMap, setCachedStatutesMap] = useState<Map<number, Statute>>(new Map());
  const [showSelectAllDialog, setShowSelectAllDialog] = useState(false);
  const [tooManyCasesSnackbarOpen, setTooManyCasesSnackbarOpen] = useState(false);
  const [pendingSelectAllStatutes, setPendingSelectAllStatutes] = useState<Statute[]>([]);
  const [pendingSelectAllContext, setPendingSelectAllContext] = useState<{
    caseType: 'comparator' | 'exclusion';
    caseInputId: number;
    isDeselecting: boolean;
  } | null>(null);
  const [disparityFields, setDisparityFields] = useState<DisparityFieldsData>(
    getDefaultDisparityFields(initialDisparityFields),
  );
  const [selectedDyad, setSelectedDyad] = useState<EnhancementDyad | null>(null);
  const [hasDateError, setHasDateError] = useState(false);

  // Verification / error report dialog state
  const [showNoComparisonDataDialog, setShowNoComparisonDataDialog] = useState(false);
  const [showSendErrorReportDialog, setShowSendErrorReportDialog] = useState(false);
  const [showFeedbackSentDialog, setShowFeedbackSentDialog] = useState(false);

  const { applyDisparityCalculation, postDisparityForCase } = useCaseApi();
  const { sendDisparityCalculationFeedback } = useFeedbackApi();

  const effectiveCountyId = existingDisparity?.case.county.id ?? countyId ?? caseData?.county.id;

  const { processedRaces } = useProcessedRaces({ caseData, includeComparisonRaces: true });
  const racesList = configRaces ?? processedRaces;

  const { minDate, maxDate } = useCalculatedDateRange({
    caseData: caseData,
  });

  const existingPoolStatutes = useMemo(() => {
    if (!existingDisparity?.comparison_pools) return [];

    const statuteMap = new Map<number, Statute>();

    existingDisparity.comparison_pools.forEach((pool) => {
      pool.comparison_pool_statutes.forEach((s) => {
        if (!statuteMap.has(s.statute_id)) {
          statuteMap.set(s.statute_id, {
            id: s.statute_id,
            display_name: s.display_name,
            type: s.type,
            statute: s.statute,
            is_wobbler: s.is_wobbler,
            is_enhancement: s.is_enhancement,
            cases_count: s.cases_count,
            description: s.description,
            created_at: s.created_at,
            updated_at: s.updated_at,
          });
        }
      });
    });

    return Array.from(statuteMap.values());
  }, [existingDisparity]);

  const statuteMap = useMemo(() => {
    const map = new Map<number, Statute>();

    if (existingDisparity?.case.statutes) {
      existingDisparity.case.statutes.forEach((s) => map.set(s.id, s));
    }

    existingPoolStatutes.forEach((s) => map.set(s.id, s));

    cachedStatutesMap.forEach((statute, id) => {
      if (!map.has(id)) {
        map.set(id, statute);
      }
    });

    return map;
  }, [existingDisparity, existingPoolStatutes, cachedStatutesMap]);

  const formatStatuteIds = useCallback((statuteIds: number[]) => [...statuteIds].sort((a, b) => a - b).join(','), []);

  // Create dyads for enhancement subtype if case data is available
  const dyads = useMemo<EnhancementDyad[]>(
    () => createEnhancementDyadsFromCase(caseData ?? existingDisparity?.case),
    [caseData, existingDisparity?.case],
  );

  const isWobblerSelected = disparityFields.subtypeId === DisparitySubType.WOBBLERS;
  const isEnhancementSelected = disparityFields.subtypeId === DisparitySubType.ENHANCEMENT;
  const isPoolDisabled = isWobblerSelected || isEnhancementSelected;

  const savedPools = useMemo(() => {
    if (!existingDisparity) return null;
    const ofType = (typeId: number) =>
      existingDisparity.comparison_pools
        .filter((pool) => pool.comparison_pool_type.id === typeId)
        .map((pool) => pool.comparison_pool_statutes.map((s) => s.statute_id));
    const baseList = ofType(ComparisonPoolType.BASE);
    return {
      base: baseList[0] ?? [],
      inclusion: ofType(ComparisonPoolType.INCLUSION),
      exclusion: ofType(ComparisonPoolType.EXCLUSION),
    };
  }, [existingDisparity]);

  const poolsChanged = useMemo(() => {
    if (!savedPools) return false;
    return (
      JSON.stringify(comparatorCaseInputs) !== JSON.stringify(savedPools.inclusion) ||
      JSON.stringify(exclusionCaseInputs) !== JSON.stringify(savedPools.exclusion) ||
      JSON.stringify(baseStatuteIds) !== JSON.stringify(savedPools.base)
    );
  }, [savedPools, comparatorCaseInputs, exclusionCaseInputs, baseStatuteIds]);

  const [prevIsPoolDisabled, setPrevIsPoolDisabled] = useState(isPoolDisabled);
  if (isPoolDisabled !== prevIsPoolDisabled) {
    setPrevIsPoolDisabled(isPoolDisabled);
    if (isPoolDisabled) {
      setComparatorCaseInputs([]);
      setExclusionCaseInputs([]);
    }
  }

  const caseInputsValidationError = useMemo(() => {
    if (isPoolDisabled) return null;

    const formattedInclusionCases = comparatorCaseInputs.map(formatStatuteIds);
    const formattedExclusionCases = exclusionCaseInputs.map(formatStatuteIds);

    if (comparatorCaseInputs.some((row) => row.length === 0) || exclusionCaseInputs.some((row) => row.length === 0)) {
      return 'Remove empty case or select statutes.';
    }

    const uniqueInclusion = new Set<string>();
    for (const set of formattedInclusionCases) {
      if (set && uniqueInclusion.has(set)) {
        return 'Remove duplicate case from the Comparator pool.';
      }
      if (set) uniqueInclusion.add(set);
    }

    const uniqueExclusion = new Set<string>();
    for (const set of formattedExclusionCases) {
      if (set && uniqueExclusion.has(set)) {
        return 'Remove duplicate case from the Exclusion pool.';
      }
      if (set) uniqueExclusion.add(set);
    }

    for (const compSet of formattedInclusionCases) {
      if (compSet && uniqueExclusion.has(compSet)) {
        return 'Remove duplicate case found between Comparator and Exclusion pools.';
      }
    }

    return null;
  }, [isPoolDisabled, comparatorCaseInputs, exclusionCaseInputs, formatStatuteIds]);

  const handleComparatorToolDialogToggle = () => {
    setOpenComparatorToolDialog((prev) => !prev);
  };

  const handleCancel = () => {
    setOpenComparatorToolDialog(false);
    setDisparityFields(getDefaultDisparityFields(initialDisparityFields));
    setComparatorCaseInputs(initialComparisonPools?.inclusion ?? []);
    setExclusionCaseInputs(initialComparisonPools?.exclusion ?? []);
    setBaseStatuteIds(initialComparisonPools?.base?.[0] ?? []);
    setSelectedDyad(null);
  };

  const handleAddComparatorCase = () => setComparatorCaseInputs((prev) => [...prev, []]);
  const handleAddExclusionCase = () => setExclusionCaseInputs((prev) => [...prev, []]);
  const handleRemoveCaseInput = (caseType: 'comparator' | 'exclusion', index: number) => {
    if (caseType === 'comparator') {
      setComparatorCaseInputs((prev) => prev.filter((_, i) => i !== index));
    } else {
      setExclusionCaseInputs((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleStatuteChange = (caseType: 'comparator' | 'exclusion', index: number, newValue: Statute[]) => {
    const newSelectedIds = newValue.map((statute) => statute.id);
    if (caseType === 'comparator') {
      setComparatorCaseInputs((prev) => prev.map((row, i) => (i === index ? newSelectedIds : row)));
    } else {
      setExclusionCaseInputs((prev) => prev.map((row, i) => (i === index ? newSelectedIds : row)));
    }
  };

  const handleCacheUpdate = (statutes: Statute[]) => {
    setCachedStatutesMap((prev) => {
      const updatedStatutesCache = new Map(prev);
      statutes.forEach((statute) => {
        updatedStatutesCache.set(statute.id, statute);
      });
      return updatedStatutesCache;
    });
  };

  const handleSelectAllStatutes = (
    caseType: 'comparator' | 'exclusion',
    caseInputId: number,
    statutes: Statute[],
    isDeselecting: boolean,
  ) => {
    if (isDeselecting) {
      const currentValue =
        caseType === 'comparator' ? comparatorCaseInputs[caseInputId] : exclusionCaseInputs[caseInputId];
      const statuteIds = statutes.map((s) => s.id);
      const filteredIds = currentValue.filter((id) => !statuteIds.includes(id));

      if (caseType === 'comparator') {
        setComparatorCaseInputs((prev) => prev.map((row, i) => (i === caseInputId ? filteredIds : row)));
      } else {
        setExclusionCaseInputs((prev) => prev.map((row, i) => (i === caseInputId ? filteredIds : row)));
      }
    } else {
      setPendingSelectAllStatutes(statutes);
      setPendingSelectAllContext({ caseType, caseInputId: caseInputId, isDeselecting });
      setShowSelectAllDialog(true);
    }
  };

  const getStatutesFromIds = useCallback(
    (ids: number[]) => ids.map((id) => statuteMap.get(id)).filter((s): s is Statute => s !== undefined),
    [statuteMap],
  );

  // Check if data has changed from the current disparity (for existing disparities)
  const hasDataChanged = useMemo(() => {
    if (!onApply || forNewDisparity) return true;
    if (!existingDisparity || !savedPools) return true;

    const baselineStartDate = resolveInitialStartDate(existingDisparity.start_date, existingDisparity.min_found_date);
    const baselineStartStr = formatDateOrNull(baselineStartDate);
    const baselineEndDate = existingDisparity.max_found_date ?? existingDisparity.end_date;
    const baselineEndStr = baselineEndDate ? formatDateOrNull(parseISO(baselineEndDate)) : null;

    const fieldsChanged =
      JSON.stringify(disparityFields.statuteIds) !== JSON.stringify(savedPools.base) ||
      disparityFields.stageId !== (existingDisparity.stage?.id ?? null) ||
      disparityFields.typeId !== existingDisparity.type.id ||
      disparityFields.subtypeId !== (existingDisparity.subtype?.id ?? null) ||
      disparityFields.raceId !== (existingDisparity.race?.id ?? null) ||
      formatDateOrNull(disparityFields.startDate) !== baselineStartStr ||
      formatDateOrNull(disparityFields.endDate) !== baselineEndStr;

    return fieldsChanged || poolsChanged;
  }, [onApply, forNewDisparity, existingDisparity, savedPools, poolsChanged, disparityFields]);

  const buildComparisonPoolsPayload = useCallback(() => {
    const inclusion = isPoolDisabled ? [] : comparatorCaseInputs;
    const exclusion = isPoolDisabled ? [] : exclusionCaseInputs;
    return forNewDisparity ? { inclusion, exclusion } : { base: [baseStatuteIds], inclusion, exclusion };
  }, [isPoolDisabled, forNewDisparity, comparatorCaseInputs, exclusionCaseInputs, baseStatuteIds]);

  const buildNewDisparityPayload = useCallback((): NewDisparity => {
    return {
      start_date: disparityFields.startDate ? format(disparityFields.startDate, 'yyyy-MM-dd') : '',
      end_date: disparityFields.endDate ? format(disparityFields.endDate, 'yyyy-MM-dd') : '',
      race_id: disparityFields.raceId!,
      stage_id: disparityFields.stageId!,
      type_id: disparityFields.typeId!,
      subtype_id: disparityFields.subtypeId ?? undefined,
      statutes_ids: forNewDisparity ? disparityFields.statuteIds : baseStatuteIds,
      comparison_pools: buildComparisonPoolsPayload(),
    };
  }, [disparityFields, forNewDisparity, baseStatuteIds, buildComparisonPoolsPayload]);

  const queryClient = useQueryClient();
  const effectiveCaseId = caseData?.id ?? existingDisparity?.case?.id;

  const isUnprocessableEntityError = useCallback(
    (error: unknown) => error instanceof ApiClientError && error.status === 422,
    [],
  );

  const handleNonApplyCalculationError = useCallback(
    (error: unknown) => {
      if (!onError) return;

      onError(error instanceof Error ? error : new Error('Unexpected error occurred.'));
    },
    [onError],
  );

  const { mutate: createDisparity, isPending: isCreatingDisparity } = useMutation({
    mutationKey: ['postDisparityForCase', effectiveCaseId],
    mutationFn: (disparityData: NewDisparity) => postDisparityForCase(Number(effectiveCaseId), disparityData),
    onSuccess: (createdDisparity) => {
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      setOpenComparatorToolDialog(false);
      onSuccess?.(createdDisparity);
    },
    onError: (error) => {
      handleNonApplyCalculationError(error);
    },
  });

  const { mutate: applyCalculation, isPending: isApplying } = useMutation({
    mutationKey: ['applyDisparityCalculation', effectiveCaseId],
    mutationFn: (disparityData: NewDisparity) => applyDisparityCalculation(Number(effectiveCaseId), disparityData),
    onSuccess: (_response, disparityData) => {
      if (forNewDisparity) {
        createDisparity(disparityData);
      } else if (onApply) {
        onApply({
          ...disparityFields,
          comparisonPools: buildComparisonPoolsPayload(),
        });
        setOpenComparatorToolDialog(false);
      }
    },
    onError: (error) => {
      if (isUnprocessableEntityError(error)) {
        setShowNoComparisonDataDialog(true);
        return;
      }

      handleNonApplyCalculationError(error);
    },
  });

  const isSubmitting = isApplying || isCreatingDisparity;

  const isApplyDisabled = useMemo(() => {
    if (isSubmitting || !!caseInputsValidationError || !effectiveCountyId) return true;
    if (hasDateError) return true;

    if (forNewDisparity && disparityFields.statuteIds.length === 0) return true;
    if (
      forNewDisparity &&
      disparityTypes?.find((t) => t.id === disparityFields.typeId)?.subtypes?.length &&
      disparityFields.subtypeId === null
    )
      return true;

    if (onApply) {
      const effectiveStatuteIds = forNewDisparity ? disparityFields.statuteIds : baseStatuteIds;
      if (effectiveStatuteIds.length === 0) return true;
      if (!hasDataChanged) return true;
      if (
        disparityFields.stageId === null ||
        disparityFields.typeId === null ||
        disparityFields.raceId === null ||
        disparityFields.startDate === null ||
        disparityFields.endDate === null
      )
        return true;
      if (
        disparityTypes?.find((t) => t.id === disparityFields.typeId)?.subtypes?.length &&
        disparityFields.subtypeId === null
      )
        return true;
      if (
        disparityFields.typeId === DisparityType.SEVERITY &&
        disparityFields.subtypeId === DisparitySubType.TARGET_VERSUS_COMPARATORS &&
        !isPoolDisabled &&
        comparatorCaseInputs.length === 0
      )
        return true;
      return false;
    }

    // Existing disparity: disabled when nothing changed or no focus statutes
    if (!existingDisparity) return false;
    if (baseStatuteIds.length === 0) return true;
    return !poolsChanged;
  }, [
    isSubmitting,
    caseInputsValidationError,
    effectiveCountyId,
    hasDateError,
    forNewDisparity,
    onApply,
    hasDataChanged,
    disparityFields,
    disparityTypes,
    isPoolDisabled,
    comparatorCaseInputs,
    baseStatuteIds,
    existingDisparity,
    poolsChanged,
  ]);

  const handleApply = () => {
    const payload = buildNewDisparityPayload();
    applyCalculation(payload);
  };

  const handleCloseNoComparisonDataDialog = () => {
    setShowNoComparisonDataDialog(false);
  };

  const handleSendErrorReport = () => {
    setShowNoComparisonDataDialog(false);
    setShowSendErrorReportDialog(true);
  };

  const { mutate: sendDisparityFeedback, isPending: isSendingFeedback } = useMutation({
    mutationKey: ['sendDisparityCalculationFeedback'],
    mutationFn: (data: { title: string; feedbackTypeId: number; additionalComments: string }) =>
      sendDisparityCalculationFeedback({
        title: data.title,
        case_id: Number(effectiveCaseId),
        type_id: data.feedbackTypeId,
        disparity_parameters: buildNewDisparityPayload(),
        feedback: data.additionalComments,
      }),
    onSuccess: () => {
      setShowSendErrorReportDialog(false);
      setShowFeedbackSentDialog(true);
    },
  });

  const handleFeedbackContinueEditing = () => {
    setShowFeedbackSentDialog(false);
  };

  const handleFeedbackReturnToCase = () => {
    setShowFeedbackSentDialog(false);
    handleCancel();
  };

  // Statistics and filtering logic
  const countyFips = existingDisparity?.case.county.fips ?? caseData?.county.fips;

  const selectedStageName = disparityStages?.find((s) => s.id === disparityFields.stageId)?.name ?? null;
  const selectedRaceName = racesList?.find((r) => r.id === disparityFields.raceId)?.name ?? null;
  const clientRaceName = caseData?.client_race.name ?? existingDisparity?.case.client_race.name ?? null;

  // Cases matching criteria box - union of base statutes and inclusion pools minus exclusion pools,
  // where each pool contains the cases with all statutes in that pool
  const inclusionGroups = useMemo(() => comparatorCaseInputs.filter((ids) => ids.length > 0), [comparatorCaseInputs]);

  const exclusionGroups = useMemo(() => exclusionCaseInputs.filter((ids) => ids.length > 0), [exclusionCaseInputs]);

  const {
    combinedStatistics,
    statisticsData,
    isLoading: isStatisticsLoading,
  } = useCombinedStatistics({
    caseId: existingDisparity?.case.id ?? caseData?.id,
    baseStatuteIds,
    inclusionGroups,
    exclusionGroups,
    countyFips,
    enabled: openComparatorToolDialog && !forNewDisparity && !!existingDisparity,
  });

  const totalCasesCount = useMemo(() => {
    if (!combinedStatistics) return null;
    return countStatuteStatistics(combinedStatistics, {
      stageName: selectedStageName,
      raceName: null,
      startDate: disparityFields.startDate,
      endDate: disparityFields.endDate,
    });
  }, [combinedStatistics, selectedStageName, disparityFields.startDate, disparityFields.endDate]);

  const clientRaceCount = useMemo(() => {
    if (!combinedStatistics) return null;
    return countStatuteStatistics(combinedStatistics, {
      stageName: selectedStageName,
      raceName: clientRaceName,
      startDate: disparityFields.startDate,
      endDate: disparityFields.endDate,
    });
  }, [combinedStatistics, selectedStageName, clientRaceName, disparityFields.startDate, disparityFields.endDate]);

  const comparisonRaceCount = useMemo(() => {
    if (!combinedStatistics) return null;
    return countStatuteStatistics(combinedStatistics, {
      stageName: selectedStageName,
      raceName: selectedRaceName,
      startDate: disparityFields.startDate,
      endDate: disparityFields.endDate,
    });
  }, [combinedStatistics, selectedStageName, selectedRaceName, disparityFields.startDate, disparityFields.endDate]);

  type CasesCounts = { total: number; client: number; comparison: number };
  const compareCaseCounts = (a: CasesCounts, b: CasesCounts) =>
    a.total === b.total && a.client === b.client && a.comparison === b.comparison;
  const [initialCounts, setInitialCounts] = useState<CasesCounts | null>(null);
  const [lastCounts, setLastCounts] = useState<CasesCounts | null>(null);
  const [previousCounts, setPreviousCounts] = useState<CasesCounts | null>(null);

  if (totalCasesCount !== null && clientRaceCount !== null && comparisonRaceCount !== null) {
    const incoming: CasesCounts = {
      total: totalCasesCount,
      client: clientRaceCount,
      comparison: comparisonRaceCount,
    };
    if (lastCounts === null) {
      setInitialCounts(incoming);
      setLastCounts(incoming);
      setPreviousCounts(incoming);
    } else if (!compareCaseCounts(incoming, lastCounts)) {
      const revertedToInitial = initialCounts !== null && compareCaseCounts(incoming, initialCounts);
      setPreviousCounts(revertedToInitial ? incoming : lastCounts);
      setLastCounts(incoming);
    }
  }

  const totalCasesDiff =
    totalCasesCount !== null && previousCounts !== null ? totalCasesCount - previousCounts.total : null;
  const clientRaceDiff =
    clientRaceCount !== null && previousCounts !== null ? clientRaceCount - previousCounts.client : null;
  const comparisonRaceDiff =
    comparisonRaceCount !== null && previousCounts !== null ? comparisonRaceCount - previousCounts.comparison : null;

  const initializeDyad = (subtypeId: number | null | undefined, baseIds: number[]) => {
    setSelectedDyad(subtypeId === DisparitySubType.ENHANCEMENT ? findDyadByStatuteIds(dyads, baseIds) : null);
  };

  const [prevOpenDialog, setPrevOpenDialog] = useState(openComparatorToolDialog);
  if (openComparatorToolDialog && !prevOpenDialog) {
    if (initialDisparityFields) {
      setDisparityFields(getDefaultDisparityFields(initialDisparityFields));
    }
    if (forNewDisparity) {
      setComparatorCaseInputs(initialComparisonPools?.inclusion ?? []);
      setExclusionCaseInputs(initialComparisonPools?.exclusion ?? []);
    }
    setInitialCounts(null);
    setLastCounts(null);
    setPreviousCounts(null);
    setTooManyCasesSnackbarOpen(false);
    if (forNewDisparity) {
      setSelectedDyad(initialEnhancementDyad ?? null);
    } else {
      initializeDyad(existingDisparity?.subtype?.id, savedPools?.base ?? []);
    }
  }
  if (openComparatorToolDialog !== prevOpenDialog) {
    setPrevOpenDialog(openComparatorToolDialog);
  }

  // Initialize from existingDisparity
  const [prevExistingDisparity, setPrevExistingDisparity] = useState(existingDisparity);
  if (existingDisparity !== prevExistingDisparity) {
    setPrevExistingDisparity(existingDisparity);

    if (existingDisparity?.comparison_pools) {
      const initialComparatorCases: number[][] = [];
      const initialExclusionCases: number[][] = [];

      const basePool = existingDisparity.comparison_pools.find(
        (pool) => pool.comparison_pool_type.id === ComparisonPoolType.BASE,
      );

      existingDisparity.comparison_pools.forEach((item) => {
        const selectedStatuteIds = item.comparison_pool_statutes.map((s) => s.statute_id);
        if (item.comparison_pool_type.id === ComparisonPoolType.INCLUSION) {
          initialComparatorCases.push(selectedStatuteIds);
        } else if (item.comparison_pool_type.id === ComparisonPoolType.EXCLUSION) {
          initialExclusionCases.push(selectedStatuteIds);
        }
      });

      const baseIds = basePool?.comparison_pool_statutes.map((s) => s.statute_id) ?? [];
      setBaseStatuteIds(baseIds);
      setComparatorCaseInputs(initialComparatorCases);
      setExclusionCaseInputs(initialExclusionCases);
      initializeDyad(existingDisparity.subtype?.id, baseIds);
    }
  }

  return (
    <>
      <Button
        disabled={existingDisparity?.is_locked || !effectiveCountyId}
        variant='outlined'
        disableElevation
        startIcon={<PlusIcon size={16} />}
        onClick={handleComparatorToolDialogToggle}
        sx={{ fontWeight: 600 }}
      >
        Advanced Filtering
      </Button>
      <Dialog
        fullScreen
        open={openComparatorToolDialog}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
        }}
        sx={{ margin: 2, display: 'flex', flexDirection: 'column' }}
      >
        <Box
          sx={{
            background: 'linear-gradient(90deg, #13507C 0%, #12294C 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            color: '#fff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BookOpenCheckIcon />
            <Typography
              variant='h6'
              sx={{
                fontWeight: 700,
              }}
            >
              Advanced Filtering
            </Typography>
          </Box>
        </Box>
        <DialogContent
          sx={{
            paddingX: '0px',
            paddingTop: '0',
            paddingBottom: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexGrow: 1,
              overflow: 'hidden',
            }}
          >
            {/* Main section */}
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                marginTop: '16px',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              {!forNewDisparity && (
                <Box sx={{ display: 'flex', gap: '16px', marginLeft: '16px', flexWrap: 'wrap' }}>
                  {(
                    [
                      { label: 'Total Cases', count: totalCasesCount, diff: totalCasesDiff },
                      { label: 'Cases matching client race', count: clientRaceCount, diff: clientRaceDiff },
                      {
                        label: 'Cases matching comparison race',
                        count: comparisonRaceCount,
                        diff: comparisonRaceDiff,
                      },
                    ] as const
                  ).map(({ label, count, diff }) => (
                    <Box
                      key={label}
                      sx={{
                        backgroundColor: '#fff',
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
                        padding: '16px',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        flex: '1 1 220px',
                        minWidth: '220px',
                        maxWidth: '300px',
                      }}
                    >
                      <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        {label}
                      </Typography>
                      <Typography component='div' variant='body1' sx={{ fontSize: '28px', fontWeight: 700 }}>
                        {count !== null ? (
                          count.toLocaleString()
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <Typography
                              variant='body1'
                              sx={{ fontSize: '28px', fontWeight: 700, color: 'text.secondary', marginRight: '8px' }}
                            >
                              0
                            </Typography>
                            <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
                          </Box>
                        )}
                      </Typography>
                      {diff !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px' }}>
                          {diff > 0 ? (
                            <>
                              <TrendingUpIcon size={16} color='#2e7d32' />
                              <Typography variant='body2' sx={{ fontWeight: 600, color: 'success.main' }}>
                                {diff}
                              </Typography>
                            </>
                          ) : diff < 0 ? (
                            <>
                              <TrendingDownIcon size={16} color='#dc2627' />
                              <Typography variant='body2' sx={{ fontWeight: 600, color: 'error.main' }}>
                                {Math.abs(diff)}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <TrendingUpDownIcon size={16} color='#666666' />
                              <Typography variant='body2' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                No change
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}

              {/* Data Availability Heatmap */}
              {!forNewDisparity && (
                <DataAvailabilityHeatmap
                  statisticsData={statisticsData}
                  startDate={disparityFields.startDate}
                  endDate={disparityFields.endDate}
                  isLoading={isStatisticsLoading}
                />
              )}

              {/* Comparison Pools Section */}
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  alignContent: 'end',
                }}
              >
                <ComparisonPoolSection
                  type='comparator'
                  cases={comparatorCaseInputs}
                  countyId={effectiveCountyId}
                  openAutocompleteId={openAutocompleteId}
                  getStatutesFromIds={getStatutesFromIds}
                  onAddCase={handleAddComparatorCase}
                  onRemoveCase={(idx) => handleRemoveCaseInput('comparator', idx)}
                  onStatuteChange={(idx, statutes) => handleStatuteChange('comparator', idx, statutes)}
                  onCacheUpdate={handleCacheUpdate}
                  onOpenAutocompleteChange={setOpenAutocompleteId}
                  onSelectAllRequest={(idx, statutes, isDeselecting) =>
                    handleSelectAllStatutes('comparator', idx, statutes, isDeselecting)
                  }
                  disabled={isPoolDisabled}
                />
                <ComparisonPoolSection
                  type='exclusion'
                  cases={exclusionCaseInputs}
                  countyId={effectiveCountyId}
                  openAutocompleteId={openAutocompleteId}
                  getStatutesFromIds={getStatutesFromIds}
                  onAddCase={handleAddExclusionCase}
                  onRemoveCase={(idx) => handleRemoveCaseInput('exclusion', idx)}
                  onStatuteChange={(idx, statutes) => handleStatuteChange('exclusion', idx, statutes)}
                  onCacheUpdate={handleCacheUpdate}
                  onOpenAutocompleteChange={setOpenAutocompleteId}
                  onSelectAllRequest={(idx, statutes, isDeselecting) =>
                    handleSelectAllStatutes('exclusion', idx, statutes, isDeselecting)
                  }
                  disabled={isPoolDisabled}
                />
              </Box>
            </Box>

            {/* Side Panel */}
            <Box
              sx={{
                width: '400px',
                padding: '16px',
                borderLeft: '1px solid #e0e0e0',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                overflowY: 'auto',
                flexShrink: 0,
              }}
            >
              <DisparityFieldsForm
                caseData={caseData ?? existingDisparity?.case}
                disparityStages={disparityStages}
                disparityTypes={disparityTypes}
                processedRaces={racesList}
                values={disparityFields}
                onChange={(field, value) => {
                  setDisparityFields((prev) => ({ ...prev, [field]: value }));
                }}
                minDate={minDate}
                maxDate={maxDate}
                minFoundDate={existingDisparity?.min_found_date}
                showInfoBox={!forNewDisparity}
                onDateErrorChange={setHasDateError}
                baseStatuteIds={baseStatuteIds}
                onBaseStatuteChange={setBaseStatuteIds}
                singleStatuteOnly={isWobblerSelected}
                enhancementDyads={dyads}
                selectedEnhancementDyad={selectedDyad}
                onEnhancementDyadChange={setSelectedDyad}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderTop: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {caseInputsValidationError && (
              <Typography color='error' variant='body2' sx={{ fontWeight: 500 }}>
                {caseInputsValidationError}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Button
              disableElevation
              variant='outlined'
              color='primary'
              sx={{ padding: '8px 36px', fontWeight: 500 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              disableElevation
              variant='contained'
              color='primary'
              sx={{ padding: '8px 36px', fontWeight: 500 }}
              onClick={handleApply}
              disabled={isApplyDisabled}
            >
              {isSubmitting ? 'Applying...' : 'Apply'}
            </Button>
          </Box>
        </DialogActions>

        {/* Too many comparison cases snackbar */}
        <Snackbar
          open={tooManyCasesSnackbarOpen}
          autoHideDuration={6000}
          onClose={(_event, reason) => {
            if (reason === 'clickaway') return;
            setTooManyCasesSnackbarOpen(false);
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setTooManyCasesSnackbarOpen(false)} severity='warning' sx={{ width: '100%' }}>
            Too many statutes selected. Please reduce the number of comparator cases.
          </Alert>
        </Snackbar>
      </Dialog>
      {/* Select All Statutes Dialog */}
      <Dialog open={showSelectAllDialog} onClose={() => setShowSelectAllDialog(false)} maxWidth='sm' fullWidth>
        <DialogContent sx={{ padding: '24px' }}>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            Select All Statutes
          </Typography>
          <Typography variant='body2' sx={{ marginBottom: '24px', color: 'text.secondary' }}>
            You are selecting {pendingSelectAllStatutes.length} statute
            {pendingSelectAllStatutes.length !== 1 ? 's' : ''}. How would you like to add them?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Button
              variant='outlined'
              color='accentBlue'
              onClick={() => {
                if (pendingSelectAllContext) {
                  handleCacheUpdate(pendingSelectAllStatutes);
                  const newSelectedIds = pendingSelectAllStatutes.map((s) => s.id);
                  const currentValue =
                    pendingSelectAllContext.caseType === 'comparator'
                      ? comparatorCaseInputs[pendingSelectAllContext.caseInputId]
                      : exclusionCaseInputs[pendingSelectAllContext.caseInputId];
                  const combinedIds = [...currentValue, ...newSelectedIds];

                  if (pendingSelectAllContext.caseType === 'comparator') {
                    setComparatorCaseInputs((prev) =>
                      prev.map((row, i) => (i === pendingSelectAllContext.caseInputId ? combinedIds : row)),
                    );
                  } else {
                    setExclusionCaseInputs((prev) =>
                      prev.map((row, i) => (i === pendingSelectAllContext.caseInputId ? combinedIds : row)),
                    );
                  }
                }
                setShowSelectAllDialog(false);
                setPendingSelectAllStatutes([]);
                setPendingSelectAllContext(null);
              }}
              sx={{ padding: '12px', justifyContent: 'flex-start' }}
            >
              <Box>
                <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'left' }}>
                  Add as a single case
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'left' }}>
                  All selected statutes will be added to the current case
                </Typography>
              </Box>
            </Button>
            <Button
              variant='outlined'
              color='accentBlue'
              onClick={() => {
                if (pendingSelectAllContext) {
                  // check comparator+exclusion for MAX_COMPARASON_CASES
                  const totalExistingCases = comparatorCaseInputs.length + exclusionCaseInputs.length;
                  const resultingCaseCount = totalExistingCases + Math.max(0, pendingSelectAllStatutes.length - 1);
                  if (resultingCaseCount > MAX_COMPARISON_CASES) {
                    setShowSelectAllDialog(false);
                    setPendingSelectAllStatutes([]);
                    setPendingSelectAllContext(null);
                    setTooManyCasesSnackbarOpen(true);
                    return;
                  }

                  handleCacheUpdate(pendingSelectAllStatutes);

                  if (pendingSelectAllStatutes.length > 0) {
                    const firstStatuteId = pendingSelectAllStatutes[0].id;
                    const newCases = pendingSelectAllStatutes.slice(1).map((s) => [s.id]);

                    if (pendingSelectAllContext.caseType === 'comparator') {
                      setComparatorCaseInputs((prev) => {
                        const previousStatutesList = [...prev];
                        previousStatutesList[pendingSelectAllContext.caseInputId] = [
                          ...previousStatutesList[pendingSelectAllContext.caseInputId],
                          firstStatuteId,
                        ];
                        previousStatutesList.splice(pendingSelectAllContext.caseInputId + 1, 0, ...newCases);
                        return previousStatutesList;
                      });
                    } else {
                      setExclusionCaseInputs((prev) => {
                        const previousStatutesList = [...prev];
                        previousStatutesList[pendingSelectAllContext.caseInputId] = [
                          ...previousStatutesList[pendingSelectAllContext.caseInputId],
                          firstStatuteId,
                        ];
                        previousStatutesList.splice(pendingSelectAllContext.caseInputId + 1, 0, ...newCases);
                        return previousStatutesList;
                      });
                    }
                  }
                }
                setShowSelectAllDialog(false);
                setPendingSelectAllStatutes([]);
                setPendingSelectAllContext(null);
              }}
              sx={{ padding: '12px', justifyContent: 'flex-start' }}
            >
              <Box>
                <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'left' }}>
                  Add as separate cases
                </Typography>
                <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: 'left' }}>
                  Each selected statute will create a new case row
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={() => {
              setShowSelectAllDialog(false);
              setPendingSelectAllStatutes([]);
              setPendingSelectAllContext(null);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {/* Verification / Error Report Dialogs */}
      <NoComparisonDataDialog
        open={showNoComparisonDataDialog}
        onClose={handleCloseNoComparisonDataDialog}
        onSendErrorReport={handleSendErrorReport}
      />
      <SendErrorReportDialog
        open={showSendErrorReportDialog}
        onClose={() => setShowSendErrorReportDialog(false)}
        onSend={(data) => sendDisparityFeedback(data)}
        isSending={isSendingFeedback}
      />
      <FeedbackSentDialog
        open={showFeedbackSentDialog}
        onContinueEditing={handleFeedbackContinueEditing}
        onReturnToCase={handleFeedbackReturnToCase}
      />
    </>
  );
}

export default ComparatorTool;
